import { useEffect, useMemo, useState } from 'react';
import { Alert, Grid } from '@mui/material';
import { TIPO_SECAO_PAGINA_LABEL, TipoSecaoPaginaCustomizada } from '../../../../model/apm/apm-enums';
import { ICustomPageSectionResponse } from '../../../../model/apm/custom-page/response/custom-page-section-response';
import { ConfiguredFields } from '../../components/configured-fields';
import { FormDrawer, FormDrawerGroup, VisibilitySwitch } from '../../../../components/form-drawer';
import { pickFieldGroup, validateFields } from '../../components/field-config';
import { SectionTypeCard } from '../../components/section-type-card';
import { CUSTOM_PAGE_SECTION_CONFIG, CustomPageSectionField } from './custom-page-section-config';
import { CUSTOM_PAGE_SECTION_ICONS } from './custom-page-section-icons';

const SECTION_TYPES = Object.keys(CUSTOM_PAGE_SECTION_CONFIG).map(Number) as TipoSecaoPaginaCustomizada[];
const IDENTIFICATION_FIELDS: CustomPageSectionField[] = ['title', 'subtitle'];

interface CustomPageSectionDrawerProps {
    open: boolean;
    section?: ICustomPageSectionResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: Record<string, any>) => void;
}

export function CustomPageSectionDrawer({ open, section, isSaving, onClose, onSave }: CustomPageSectionDrawerProps) {
    const [form, setForm] = useState<Record<string, any>>({});
    const [showErrors, setShowErrors] = useState(false);
    const [uploading, setUploading] = useState(false);
    const isEdit = !!section;

    useEffect(() => {
        if (!open) return;
        setForm(section ? { ...section } : { type: TipoSecaoPaginaCustomizada.TextoLivre, isVisible: true });
        setShowErrors(false);
        setUploading(false);
    }, [open, section]);

    const type: TipoSecaoPaginaCustomizada = form.type ?? TipoSecaoPaginaCustomizada.TextoLivre;
    const config = CUSTOM_PAGE_SECTION_CONFIG[type];
    const identificationFields = pickFieldGroup(config.fields, IDENTIFICATION_FIELDS, true);
    const contentFields = pickFieldGroup(config.fields, IDENTIFICATION_FIELDS, false);
    const hasContent = Object.keys(contentFields).length > 0;
    const errors = useMemo(() => validateFields(config.fields, form), [config, form]);
    const itemNoun = config.itemNoun?.toLowerCase();

    function handleChange(patch: Record<string, any>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSelectType(next: TipoSecaoPaginaCustomizada) {
        // Troca de tipo descarta campos que não se aplicam ao novo tipo
        setForm((current) => ({ type: next, isVisible: current.isVisible, title: current.title, subtitle: current.subtitle }));
        setShowErrors(false);
    }

    function handleSave() {
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            return;
        }
        onSave(form);
    }

    const fieldProps = { idPrefix: 'custom-page-section', value: form, errors, showErrors, onChange: handleChange, onUploadingChange: setUploading };

    return (
        <FormDrawer
            open={open}
            icon={CUSTOM_PAGE_SECTION_ICONS[type]}
            title={isEdit ? 'Editar seção' : 'Nova seção'}
            subtitle={isEdit
                ? `${TIPO_SECAO_PAGINA_LABEL[type]}${config.itemNoun ? ` · ${section?.items?.length ?? 0} ${itemNoun}(s)` : ''}`
                : 'Escolha o tipo e preencha o conteúdo da seção'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar seção'}
            isSaving={isSaving}
            saveDisabled={uploading}
            onClose={onClose}
            onSave={handleSave}
        >
            {isEdit ? (
                <Alert severity="info" icon={CUSTOM_PAGE_SECTION_ICONS[type]}>
                    <strong>{TIPO_SECAO_PAGINA_LABEL[type]}:</strong> {config.description} O tipo não pode ser alterado depois de criado.
                </Alert>
            ) : (
                <FormDrawerGroup title="Tipo de seção" description="Define o layout da seção na página. Não pode ser alterado depois.">
                    <Grid container spacing={1.5}>
                        {SECTION_TYPES.map((t) => (
                            <Grid item xs={12} sm={6} key={t}>
                                <SectionTypeCard
                                    icon={CUSTOM_PAGE_SECTION_ICONS[t]}
                                    label={TIPO_SECAO_PAGINA_LABEL[t]}
                                    description={CUSTOM_PAGE_SECTION_CONFIG[t].description}
                                    selected={t === type}
                                    onSelect={() => handleSelectType(t)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </FormDrawerGroup>
            )}

            <FormDrawerGroup title="Identificação" description="Título e subtítulo exibidos acima da seção na página (opcionais).">
                <Grid container spacing={2}>
                    <ConfiguredFields fields={identificationFields} {...fieldProps} />
                </Grid>
            </FormDrawerGroup>

            {hasContent && (
                <FormDrawerGroup title="Conteúdo">
                    <Grid container spacing={2}>
                        <ConfiguredFields fields={contentFields} {...fieldProps} />
                    </Grid>
                </FormDrawerGroup>
            )}

            {config.itemNoun && (
                <Alert severity="success" variant="outlined">
                    {isEdit
                        ? `Os itens desta seção (${itemNoun}s) são gerenciados na lista de seções, expandindo a seção.`
                        : `Depois de salvar, expanda a seção na lista para adicionar os itens (${itemNoun}s).`}
                </Alert>
            )}

            <FormDrawerGroup title="Exibição">
                <VisibilitySwitch
                    checked={form.isVisible !== false}
                    onChange={(isVisible) => handleChange({ isVisible })}
                    description="Seções ocultas continuam salvas, mas não aparecem na página."
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
